// The record (MAJLIS-67; UI-S3, UI-C13, D-05, D-16, the approved mock-up's frames 2a and 2b): a finished majlis read
// on the LIVE CANVAS'S OWN COMPONENTS - TranscriptCanvas (static: not pinned, not a live region), Bubble (every phrase
// landed, named, original first and untouched, its translation beneath, its time, Replay of its stored clip) and
// ReplayAnnouncer - with the room's chrome taken away: no seat stack, no bottom bar, no mic. The header carries back,
// the day, "{min} min · {n} seats", Search, and the reading-language chip (UI-C9) at its inline end.
//
// MAJLIS-66 (UI-C14, UI-C15; mock-up 3a-4a, 5b): the HOST's header also carries Share (the share sheet) and the options
// button (Share and Delete record, RecordSheets.tsx). A READER by read link (`access: "read"`) gets neither - Back,
// Search, the reading chip and Replay only - and a thin "Read only · shared by the host" strip under the header. NOT
// here, on purpose: export (ruling 7), Voice - a spoken translation - (ruling 11). Replay of the original stays for
// both (ruling 2), fetched with the record's credential in the Authorization header (P-23), never a URL.
//
// Search (2b, ruling 6): the header gives way to the field; the server matches the FINAL original and the translation
// this reader is shown (P-22), and every matching PHRASE is marked, the current one solid and scrolled to the middle,
// with "{i} of {n}" as a polite live region and previous / next wrapping at the ends. The mock-up marks the matched
// WORDS; this build marks the phrase (the server matches with Arabic diacritics and case ignored, and a word range in
// the original's own spelling is not something the page can recompute exactly) - flagged for the consultant.
//
// MAJLIS-153 (D-60, UI-C40, UI-C41): the HOST's header (P-21 `access: "host"` only) gains Summary between Search and
// Share; a reader by read link and the admin view keep today's bar. The summary screen (SummaryScreen.tsx) shows OVER
// the record: the record's own parts are hidden, not unmounted, and Back restores the canvas's scroll. A notable
// moment closes the summary and marks its phrase as a search's current phrase, scrolled to the middle. Consultant
// ruling 2 (2026-10-01) as amended 2026-10-02: below RECORD_DAY_SHORT_BELOW_PX (390 px; `dayShort`) the header's day
// drops its weekday ("Sep 29") and the meta line is the duration alone ("48 min"); at 390 px and up, the full day and
// the full seat line. One component, so it holds for every viewer (host, reader by read link, admin view).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Clock, TimerId } from "../clock";
import { RECORD_SEARCH_DEBOUNCE_MS, RECORD_SEARCH_MAX } from "../constants";
import { READ_LANGUAGES } from "../data/readLanguages";
import { languageName } from "../data/languages";
import { dirOf, plural, useI18n } from "../i18n";
import type { ReplayControl } from "../media/replay";
import type { ReadLinkInfo, RecordData, RecordSearchResult, SummaryAnswer } from "../protocol/records";
import { recordTranslation, recordView } from "../record/model";
import { Bubble } from "./Bubble";
import { TranscriptCanvas } from "./Canvas";
import { LanguagePicker } from "./LanguagePicker";
import { ReplayAnnouncer } from "./ReplayButton";
import { BackIcon, ChevronDownIcon, ChevronUpIcon, EyeIcon, LinkIcon, MoreIcon, SearchIcon, SummaryIcon } from "./icons";
import { OptionsSheet, ShareSheet } from "./RecordSheets";
import { SummaryScreen } from "./SummaryScreen";
import { browserSaver, type FileSaver } from "../record/summaryFile";

export type RecordStatus = "loading" | "failed" | "ready";

const TEXT_VIEW = { visible: true, stage: "text", ms: 0 } as const;

// the browser's share sheet and download, made once and only when a summary opens without an injected one
let browserDefault: FileSaver | null = null;
const defaultSaver = () => (browserDefault ??= browserSaver());

export function RecordScreen({
  record,
  status,
  waiting,
  readLang,
  onReadLang,
  replay,
  reduced,
  clock,
  onBack,
  onRetry,
  onSearch,
  links = null,
  fresh = null,
  onLoadLinks = () => undefined,
  onCreateLink = async () => false,
  onRevokeLink = async () => false,
  onDropFresh = () => undefined,
  onDelete = async () => false,
  onSummary,
  dayShort = false,
  chipCompact = false,
  saver,
  header = true,
}: {
  /** MAJLIS-176 (the consultant's ruling 1 of 2026-10-06): false inside the admin, whose own bar is the one header - the
   * record is drawn without its header (no second Back, no search the admin cannot use; the admin's facts strip carries
   * the reading language and opens the same picker) */
  header?: boolean;
  /** MAJLIS-167 (the consultant's ruling B): below LANG_CHIP_COMPACT_BELOW_PX the reading chip shows its code, as the
   * room header's does (MAJLIS-109) */
  chipCompact?: boolean;
  /** MAJLIS-154 (UI-C41): the phone's share sheet or download for the summary's "Save as file" (default: the browser's) */
  saver?: FileSaver;
  /** MAJLIS-153 (P-40): one summary POST for this record in a reading language; the icon is drawn only for the host */
  onSummary?: (lang: string) => Promise<SummaryAnswer>;
  /** MAJLIS-153 (ruling 2): the viewport is narrow - the header's day without its weekday */
  dayShort?: boolean;
  /** MAJLIS-66 (host only): the record's read links (null: not loaded), and the address of one just made (shown once) */
  links?: ReadLinkInfo[] | null;
  fresh?: string | null;
  onLoadLinks?: () => void;
  onCreateLink?: (label: string | null) => Promise<boolean>;
  onRevokeLink?: (id: string) => Promise<boolean>;
  /** the share sheet closed: the fresh address is dropped for good */
  onDropFresh?: () => void;
  onDelete?: () => Promise<boolean>;
  record: RecordData | null;
  status: RecordStatus;
  /** translations are still on their way (the app is re-reading the record): pending slots show their dots */
  waiting: boolean;
  readLang: string;
  onReadLang: (tag: string) => void;
  replay: ReplayControl;
  reduced: boolean;
  clock: Clock;
  onBack: () => void;
  onRetry: () => void;
  onSearch: (q: string) => Promise<RecordSearchResult | null>;
}) {
  const { lang, t } = useI18n();
  const [sheet, setSheet] = useState(false);
  const [hostSheet, setHostSheet] = useState<"share" | "options" | null>(null);
  // MAJLIS-66: a host's record unless the server says it is read by link; MAJLIS-ADMIN: the admin view is read-only too
  const host = record?.access !== "read" && record?.access !== "admin";
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<string[] | null>(null);
  const [current, setCurrent] = useState(0);
  const jump = useRef<(() => void) | null>(null);
  const asked = useRef(0);
  // MAJLIS-153 (UI-C40): the summary is the HOST's alone - P-21 says so with `access: "host"`
  const summaryHost = record?.access === "host" && onSummary !== undefined;
  const [summaryOpen, setSummaryOpen] = useState(false);
  // a notable moment's phrase, marked as a search's current one (UI-C41) until search opens or the summary does again
  const [marked, setMarked] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const savedScroll = useRef<number | null>(null);

  const view = useMemo(() => (record ? recordView(record) : null), [record]);

  // "It matches as you type" (2b): one request after the typing pauses; an older answer never overwrites a newer one.
  useEffect(() => {
    if (!searching) return;
    const words = q.trim();
    const ask = ++asked.current;
    if (!words) return; // an empty field shows no marks (derived below), and asks nothing
    const id: TimerId = clock.setTimeout(() => {
      void onSearch(words).then((res) => {
        if (ask !== asked.current) return;
        setHits(res ? res.hits.map((h) => h.segment_id) : []);
        setCurrent(0);
      });
    }, RECORD_SEARCH_DEBOUNCE_MS);
    return () => clock.clearTimeout(id);
  }, [q, searching, clock, onSearch, readLang]);

  const typed = q.trim() !== "";
  const shownHits = useMemo(
    () => (typed && hits && view ? hits.filter((id) => view.phrases[id] !== undefined) : null),
    [typed, hits, view],
  );
  const searchCurrent = shownHits && shownHits.length > 0 ? shownHits[current % shownHits.length] : null;
  const currentId = searchCurrent ?? (searching || summaryOpen ? null : marked);

  // the current match is scrolled to the middle (jsdom has no scrollIntoView: the call is optional)
  useEffect(() => {
    if (!currentId) return;
    const el = document.querySelector<HTMLElement>(`.record-phrase[data-phrase="${currentId}"]`);
    el?.scrollIntoView?.({ block: "center", behavior: reduced ? "auto" : "smooth" });
  }, [currentId, reduced]);

  // Back from the summary: the record where it was (the canvas was hidden, which forgets its scroll)
  useLayoutEffect(() => {
    if (summaryOpen || savedScroll.current === null) return;
    const canvas = root.current?.querySelector<HTMLElement>(".canvas");
    if (canvas) canvas.scrollTop = savedScroll.current;
    savedScroll.current = null;
  }, [summaryOpen]);
  const openSummary = () => {
    savedScroll.current = root.current?.querySelector<HTMLElement>(".canvas")?.scrollTop ?? null;
    setMarked(null);
    setSummaryOpen(true);
  };
  const ask = useCallback((l: string) => onSummary!(l), [onSummary]);

  const closeSearch = () => {
    asked.current += 1;
    setSearching(false);
    setQ("");
    setHits(null);
    setCurrent(0);
  };
  const step = (by: number) => {
    if (!shownHits || shownHits.length === 0) return;
    setCurrent((c) => (c + by + shownHits.length) % shownHits.length); // wraps at the ends
  };

  const day = (iso: string | null) =>
    iso
      ? new Intl.DateTimeFormat(
          lang,
          dayShort ? { month: "short", day: "numeric" } : { weekday: "short", month: "short", day: "numeric" },
        ).format(Date.parse(iso))
      : "";
  const time = (iso: string) =>
    new Intl.DateTimeFormat(lang, { hour: "2-digit", minute: "2-digit" }).format(Date.parse(iso));
  const readName = languageName(readLang, lang);
  const readNative = READ_LANGUAGES.find((l) => l.tag === readLang)?.native ?? readName;
  const hitSet = new Set(shownHits ?? []);
  const counter =
    !searching || !q.trim() || shownHits === null
      ? ""
      : shownHits.length === 0
        ? t("noMatch")
        : t("matchOf", { i: (current % shownHits.length) + 1, n: shownHits.length });

  return (
    <div className="room record" data-spec="UI-S3 UI-C3 D-05 D-16" ref={root}>
      <div className="record-view" hidden={summaryOpen}>
      {!header ? null : searching ? (
        <header className="room-header record-search" data-spec="UI-C13 D-05 D-17">
          <button type="button" className="icon-button" aria-label={t("endSearch")} onClick={closeSearch}>
            <BackIcon />
          </button>
          <label className="visually-hidden" htmlFor="record-search">
            {t("search")}
          </label>
          <input
            id="record-search"
            className="text-input record-search-input"
            type="search"
            dir="auto"
            autoComplete="off"
            maxLength={RECORD_SEARCH_MAX}
            placeholder={t("search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") step(e.shiftKey ? -1 : 1);
              if (e.key === "Escape") closeSearch();
            }}
          />
          <span className="record-counter" role="status" aria-live="polite">
            {counter}
          </span>
          <button type="button" className="icon-button" aria-label={t("prevMatch")} onClick={() => step(-1)}>
            <ChevronUpIcon />
          </button>
          <button type="button" className="icon-button" aria-label={t("nextMatch")} onClick={() => step(1)}>
            <ChevronDownIcon />
          </button>
        </header>
      ) : (
        <header className="room-header record-header">
          <button type="button" className="icon-button" aria-label={t("back")} onClick={onBack}>
            <BackIcon />
          </button>
          <div className="room-id record-title">
            <span className="record-day">{record ? day(record.started ?? record.created) : ""}</span>
            {/* MAJLIS-167 (the consultant's ruling B): below LANG_CHIP_COMPACT_BELOW_PX the compact chip frees ~16 px, and
                "Sep 29" still does not fit beside it (measured: 40 px of 44.9 en, 56.4 ar), so the duration goes there
                and the day stays */}
            {record && !chipCompact && (
              <span className="record-meta">
                {/* ruling of 2026-10-02: below RECORD_DAY_SHORT_BELOW_PX the duration alone (the people row shows who) */}
                {dayShort
                  ? t("summaryMinutes", { min: record.minutes })
                  : plural(lang, t, "rowMeta", record.people, { min: record.minutes })}
              </span>
            )}
          </div>
          <button
            type="button"
            className="icon-button"
            aria-label={t("search")}
            disabled={!record}
            onClick={() => {
              setMarked(null);
              setSearching(true);
            }}
            data-spec="UI-C13"
          >
            <SearchIcon />
          </button>
          {record && summaryHost && (
            <button
              type="button"
              className="icon-button"
              aria-label={t("summary")}
              onClick={openSummary}
              data-spec="UI-C40 D-60 P-40"
            >
              <SummaryIcon />
            </button>
          )}
          {record && host && (
            <>
              <button
                type="button"
                className="icon-button"
                aria-label={t("share")}
                onClick={() => {
                  onLoadLinks();
                  setHostSheet("share");
                }}
                data-spec="UI-C15 P-30 D-16"
              >
                <LinkIcon />
              </button>
              <button
                type="button"
                className="icon-button"
                aria-label={t("recordMenu")}
                onClick={() => {
                  onLoadLinks();
                  setHostSheet("options");
                }}
                data-spec="UI-C14 UI-S3 D-15"
              >
                <MoreIcon />
              </button>
            </>
          )}
          <button
            type="button"
            className="reading-chip"
            aria-label={t("readingChip", { lang: readName })}
            onClick={() => setSheet(true)}
            data-spec="UI-C9 D-06 D-21"
          >
            {/* MAJLIS-167 (ruling B): the room header's compact code mode (MAJLIS-109, UI-C9) below 360 px */}
            <span lang={readLang} dir={chipCompact ? "ltr" : dirOf(readLang)}>
              {chipCompact ? readLang.toUpperCase() : readNative}
            </span>
          </button>
        </header>
      )}

      {/* MAJLIS-167 (item 4c): a READER by read link only - the admin view is the owner's own, not a shared link */}
      {record?.access === "read" && (
        <p className="readonly-strip" data-spec="UI-S3 D-16 P-33">
          <EyeIcon />
          <span>{t("readOnly")}</span>
        </p>
      )}

      <TranscriptCanvas
        live={false}
        empty={status === "ready" && !!view && view.bubbles.length === 0}
        onPinnedChange={() => undefined}
        jumpRef={jump}
        contentKey={record}
      >
        {status === "loading" && !record && <p className="canvas-empty">{t("recordLoading")}</p>}
        {status === "failed" && !record && (
          <div className="canvas-empty record-failed" role="alert">
            <p>{t("recordFailed")}</p>
            <button type="button" className="text-link" onClick={onRetry}>
              {t("tryAgain")}
            </button>
          </div>
        )}
        {record && view && (
          <>
            {record.started && (
              <p className="record-marker" data-spec="UI-S3">
                {t("started", { t: time(record.started) })}
              </p>
            )}
            {view.bubbles.map((b) => (
              <div
                key={b.id}
                className={`record-phrase${hitSet.has(b.id) ? " hit" : ""}${b.id === currentId ? " current" : ""}`}
                data-phrase={b.id}
              >
                <Bubble
                  bubble={b}
                  seat={view.seats[b.seat]}
                  view={TEXT_VIEW}
                  isMe={false}
                  levels={null}
                  reduced={reduced}
                  translation={recordTranslation(view.phrases[b.id], waiting)}
                  fromLive={false}
                  replay={replay}
                />
              </div>
            ))}
            {record.ended && (
              <p className="record-marker" data-spec="UI-S3">
                {t("ended", { t: time(record.ended) })}
              </p>
            )}
          </>
        )}
      </TranscriptCanvas>
      </div>
      {summaryOpen && record && (
        <SummaryScreen
          record={record}
          readLang={readLang}
          clock={clock}
          ask={ask}
          saver={saver ?? defaultSaver()}
          onBack={() => setSummaryOpen(false)}
          onChip={() => setSheet(true)}
          onMoment={(id) => {
            savedScroll.current = null; // the moment's phrase is scrolled to instead
            setMarked(id);
            setSummaryOpen(false);
          }}
        />
      )}
      <ReplayAnnouncer replay={replay} />

      {hostSheet === "share" && (
        <ShareSheet
          links={links}
          fresh={fresh}
          onCreate={onCreateLink}
          onRevoke={onRevokeLink}
          onClose={() => {
            onDropFresh();
            setHostSheet(null);
          }}
        />
      )}
      {hostSheet === "options" && (
        <OptionsSheet
          live={links === null ? null : links.filter((k) => !k.revoked).length}
          onShare={() => {
            onLoadLinks();
            setHostSheet("share");
          }}
          onDelete={onDelete}
          onClose={() => setHostSheet(null)}
        />
      )}
      {sheet && (
        <LanguagePicker
          title={t("read")}
          list={READ_LANGUAGES}
          value={readLang}
          spec="UI-C9 UI-C2 D-06 D-21"
          onPick={(tag) => {
            onReadLang(tag);
            setSheet(false);
          }}
          onClose={() => setSheet(false)}
        />
      )}
    </div>
  );
}
